import { useState } from 'react'
import { guardCard } from '@/hooks/cards/guardCard'
import type { useCardActions } from '@/hooks/cards/useCardActions'

type Actions = ReturnType<typeof useCardActions>

/** Buylist / selllist handlers for one card, including the buylist <-> selllist conflict confirmation. */
export function useCardListHandlers(
  cardId: number | undefined,
  actions: Actions,
  onBuylist: number,
  onSelllist: number,
) {
  const { addToBuylist, removeFromBuylist, addToSelllist, removeFromSelllist } = actions
  const [conflictTarget, setConflictTarget] = useState<'buylist' | 'selllist' | null>(null)
  const guard = guardCard(cardId)

  const handleAddToBuylist = guard((id, quantity?: number) => {
    if (onSelllist) {
      setConflictTarget('buylist')
      return
    }
    addToBuylist.mutate({ cardId: id, ...(quantity !== undefined && { quantity }) })
  })

  const handleAddToSelllist = guard((id) => {
    if (onBuylist) {
      setConflictTarget('selllist')
      return
    }
    addToSelllist.mutate({ cardId: id })
  })

  const handleConflictConfirm = async () => {
    if (!cardId || !conflictTarget) return
    if (conflictTarget === 'buylist') {
      await removeFromSelllist.mutateAsync({ cardId, all: true })
      await addToBuylist.mutateAsync({ cardId })
    } else {
      await removeFromBuylist.mutateAsync({ cardId, all: true })
      await addToSelllist.mutateAsync({ cardId })
    }
    setConflictTarget(null)
  }

  return {
    onAddToBuylist: handleAddToBuylist,
    onRemoveFromBuylist: guard((id) => removeFromBuylist.mutate(id)),
    onAddToSelllist: handleAddToSelllist,
    onRemoveFromSelllist: guard((id) => removeFromSelllist.mutate(id)),
    isAddingToBuylist: addToBuylist.isPending,
    isRemovingFromBuylist: removeFromBuylist.isPending,
    isAddingToSelllist: addToSelllist.isPending,
    isRemovingFromSelllist: removeFromSelllist.isPending,
    conflictTarget,
    setConflictTarget,
    handleConflictConfirm,
    isConflictLoading:
      removeFromBuylist.isPending || removeFromSelllist.isPending || addToBuylist.isPending || addToSelllist.isPending,
  }
}
